import * as THREE from "three";
import Experience from "../Experience.js";

export default class Measures {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.objectManager = this.experience.objectManager;
    this.room = this.experience.room;
    this.camera = this.experience.camera;

    this.visible = true;

    this.wallThickness = 0;

    // Reinit group
    this.linesMeasureSelectedObject = new THREE.Group();
    this.textMeasureSelectedObject = new THREE.Group();

    this.offsetDistanceWall = -0.4;

    this.measureObjectsShow = new Map(); // key value
  }
  // Create distance between objects and walls
  createDistanceLabel(position, distanceText) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = "40px Arial";
    context.fillStyle = "0x000000";
    context.fillText(distanceText, 0, 50);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.copy(position);
    sprite.scale.set(1, 0.6, 1);
    return sprite;
  }

  // Update Distance for an object
  updateDistances(selectedObject) {
    if (this.camera.is2DView && !this.visible) {
      return;
    }
    // Obtenir la position et les dimensions de l'objet
    const objectBox = new THREE.Box3().setFromObject(selectedObject);
    const objectSize = new THREE.Vector3();
    objectBox.getSize(objectSize);
    const objectCenter = new THREE.Vector3();
    objectBox.getCenter(objectCenter);

    // Supprimer les mesures précédentes
    if (!this.camera.is2DView) {
      this.removeMeasuresOfObject();
    } else if (this.camera.is2DView) {
      if (this.measureObjectsShow.has(selectedObject)) {
        this.removeMeasuresForObject(selectedObject);
        this.measureObjectsShow.delete(selectedObject);
        return;
      }
    }

    const linesGroup = new THREE.Group();
    const textGroup = new THREE.Group();

    // Fonction utilitaire
    const addMeasureLine = (start, end, labelText) => {
      const midpoint = start.clone().lerp(end, 0.5);
      const label = this.createDistanceLabel(midpoint, labelText);
      textGroup.add(label);

      const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
      const material = new THREE.LineBasicMaterial({
        color: 0x000000,
        depthTest: false,
      });
      const line = new THREE.Line(geometry, material);
      line.position.y = 0.2; // ou .x si tu veux
      linesGroup.add(line);
    };

    // Mesures latérales
    const directions = [
      {
        dir: new THREE.Vector3(1, 0, 0),
        offset: new THREE.Vector3(objectSize.x / 2, 0, 0),
      }, // droite
      {
        dir: new THREE.Vector3(-1, 0, 0),
        offset: new THREE.Vector3(-objectSize.x / 2, 0, 0),
      }, // gauche
      {
        dir: new THREE.Vector3(0, 0, 1),
        offset: new THREE.Vector3(0, 0, objectSize.z / 2),
      }, // avant
      {
        dir: new THREE.Vector3(0, 0, -1),
        offset: new THREE.Vector3(0, 0, -objectSize.z / 2),
      }, // arrière
    ];

    directions.forEach(({ dir, offset }) => {
      const start = objectCenter.clone().add(offset);
      const probeSize = 0.05;
      const maxDistance = 5;
      const step = 0.01;
      let closestDistance = maxDistance;
      let closestPoint = null;
      let hitType = null;

      for (let d = step; d < maxDistance; d += step) {
        const probeCenter = start.clone().add(dir.clone().multiplyScalar(d));
        const probeBox = new THREE.Box3().setFromCenterAndSize(
          probeCenter,
          new THREE.Vector3(
            dir.x !== 0 ? step : objectSize.x - probeSize,
            objectSize.y,
            dir.z !== 0 ? step : objectSize.z - probeSize
          )
        );

        for (const obj of this.objectManager.getObjects()) {
          if (obj !== selectedObject) {
            const objBox = new THREE.Box3().setFromObject(obj);
            if (probeBox.intersectsBox(objBox)) {
              closestDistance = d;
              closestPoint = probeCenter;
              hitType = "objet";
              break;
            }
          }
        }

        if (!closestPoint) {
          for (const wall of this.room.getWallsGroup().children) {
            const wallBox = new THREE.Box3().setFromObject(wall);
            if (probeBox.intersectsBox(wallBox)) {
              closestDistance = d;
              closestPoint = probeCenter;
              hitType = "mur";
              break;
            }
          }
        }

        if (closestPoint) break;
      }

      if (closestPoint && closestDistance * 1000 > 50) {
        const label = `${Math.round(closestDistance * 1000)} mm`;
        addMeasureLine(start, closestPoint, label);
      }
    });

    // ✅ Mesure verticale pour fenêtre
    if (selectedObject.name === "windowGroup") {
      const box = new THREE.Box3().setFromObject(selectedObject);
      const bottomCenter = new THREE.Vector3(
        (box.min.x + box.max.x) / 2,
        box.min.y,
        (box.min.z + box.max.z) / 2
      );

      const downDir = new THREE.Vector3(0, -1, 0);
      const raycaster = new THREE.Raycaster(bottomCenter, downDir);
      const ground = this.scene.getObjectByName("floor");

      if (ground) {
        const groundIntersects = raycaster.intersectObject(ground, true);
        if (groundIntersects.length > 0) {
          const hitPoint = groundIntersects[0].point;
          const distance = bottomCenter.distanceTo(hitPoint);
          if (distance * 1000 > 50) {
            const label = `${Math.round(distance * 1000)} mm`;
            addMeasureLine(bottomCenter, hitPoint, label);
          }
        }
      }
    }

    this.measureObjectsShow.set(selectedObject, {
      lines: linesGroup,
      text: textGroup,
    });

    if (this.camera.is2DView) {
      linesGroup.position.y = 3;
      textGroup.position.y = 3;
    }
    this.scene.add(linesGroup);
    this.scene.add(textGroup);
  }

  removeMeasuresOfObject() {
    // Parcourir tous les objets dans `measureObjectsShow`
    this.measureObjectsShow.forEach((measureGroups) => {
      // Supprimer chaque ligne du groupe de lignes et libérer les ressources
      measureGroups.lines.children.forEach((line) => {
        line.geometry.dispose();
        line.material.dispose();
      });
      // Retirer le groupe de lignes de la scène
      this.scene.remove(measureGroups.lines);

      // Supprimer chaque texte du groupe de texte et libérer les ressources
      measureGroups.text.children.forEach((text) => {
        if (text.material && text.material.map) text.material.map.dispose();
        text.material.dispose();
      });
      // Retirer le groupe de texte de la scène
      this.scene.remove(measureGroups.text);
    });

    // Vider la map `measureObjectsShow` pour effacer toutes les références
    this.measureObjectsShow.clear();
  }

  // Remove object on 2D plan
  removeMeasuresForObject(object) {
    const measureGroups = this.measureObjectsShow.get(object);
    if (measureGroups) {
      // Supprimer les lignes et les textes associés de la scène
      measureGroups.lines.children.forEach((line) => {
        line.geometry.dispose();
        line.material.dispose();
      });
      measureGroups.text.children.forEach((text) => {
        if (text.material && text.material.map) text.material.map.dispose();
        text.material.dispose();
      });

      this.scene.remove(measureGroups.lines);
      this.scene.remove(measureGroups.text);
    }
  }

  /**
   * Create measures walls
   */

  createMeasuresWalls(shapeRoom, wallPoints) {
    this.wallThickness = this.room.getWallThickness();

    this.linesGroupWalls = new THREE.Group();
    this.textGroupWalls = new THREE.Group();

    for (let i = 0; i < wallPoints.length; i++) {
      const startPoint = wallPoints[i];
      const endPoint = wallPoints[(i + 1) % wallPoints.length];

      const wallDirection = new THREE.Vector3()
        .subVectors(endPoint, startPoint)
        .normalize();
      const offsetDirection = new THREE.Vector3()
        .crossVectors(wallDirection, new THREE.Vector3(0, 1, 0))
        .normalize();

      const wallOffset = offsetDirection
        .clone()
        .multiplyScalar(this.wallThickness / 2);
      const adjustedStartPoint = startPoint.clone().add(wallOffset);
      const adjustedEndPoint = endPoint.clone().add(wallOffset);

      // Calcul de la distance et du midpoint ajusté
      const distance = adjustedStartPoint.distanceTo(adjustedEndPoint);
      const midpoint = new THREE.Vector3()
        .addVectors(adjustedStartPoint, adjustedEndPoint)
        .multiplyScalar(0.5);

      /*   const distance = startPoint.distanceTo(endPoint);
      const midpoint = new THREE.Vector3(
        (startPoint.x + endPoint.x) / 2,
        (startPoint.y + endPoint.y) / 2,
        (startPoint.z + endPoint.z) / 2
      ); */

      /*    const wallDirection = new THREE.Vector3()
        .subVectors(endPoint, startPoint)
        .normalize();
      const offsetDirection = new THREE.Vector3()
        .crossVectors(wallDirection, new THREE.Vector3(0, 1, 0))
        .normalize(); */

      // Applique un léger décalage pour placer la mesure en dehors du mur
      const offsetPosition = midpoint
        .clone()
        .add(offsetDirection.multiplyScalar(this.offsetDistanceWall));

      // Conditions pour gérer l'épaisseur du mur
      let finalDistance = Math.round((distance * 1000) / 10) * 10;
      /* if (shapeRoom === "L") {
        if (i != 2 && i + 1 != 3 && i != 3 && i + 1 != 4) {
          finalDistance -= this.wallThickness * 2 * 1000;
        }
      } else if (shapeRoom == "rectangle") {
        finalDistance -= this.wallThickness * 2 * 1000;
      } else if (shapeRoom === "T") {
        if (i != 2 && i + 1 != 3 && i != 6 && i + 1 != 7) {
          if ((i === 3 && i + 1 === 4) || (i === 5 && i + 1 === 6)) {
            finalDistance -= this.wallThickness * 1000;
          } else {
            finalDistance -= this.wallThickness * 2 * 1000;
          }
        }
      } */

      // Crée l'étiquette de mesure avec le décalage
      if (distance !== 0) {
        const distanceLabel = this.createDistanceLabel(
          offsetPosition,
          `${finalDistance} mm`
        );
        // Calcule un angle de rotation pour orienter le label parallèle au mur
        const angle = Math.atan2(wallDirection.z, wallDirection.x);
        distanceLabel.rotation.y = angle;

        this.textGroupWalls.add(distanceLabel);

        // line
        const geometry = new THREE.BufferGeometry().setFromPoints([
          startPoint,
          endPoint,
        ]);
        const material = new THREE.LineBasicMaterial({ color: 0x000000 });
        const line = new THREE.Line(geometry, material);

        line.name = "lineMeasure";

        this.linesGroupWalls.add(line);
      }
    }

    // Ajout des groupes de lignes et de texte à la scène

    this.linesGroupWalls.position.copy(
      this.scene.getObjectByName("wallsGroup").position
    );

    this.textGroupWalls.position.set(
      this.scene.getObjectByName("wallsGroup").position.x -
        this.offsetDistanceWall / 2,
      3,
      this.scene.getObjectByName("wallsGroup").position.z -
        this.offsetDistanceWall / 2
    );
    this.linesGroupWalls.position.y = 3;
    this.textGroupWalls.position.y = 3;
    this.scene.add(this.linesGroupWalls);
    this.scene.add(this.textGroupWalls);
  }

  /**
   * Remove measure walls on 2D plan
   */
  removeMeasuresWalls() {
    if (this.linesGroupWalls) {
      this.scene.remove(this.linesGroupWalls);
      this.linesGroupWalls.traverse((child) => {
        if (child.isMesh) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.linesGroupWalls.clear();
      this.linesGroupWalls = null;
    }

    if (this.textGroupWalls) {
      this.scene.remove(this.textGroupWalls);
      this.textGroupWalls.traverse((child) => {
        if (child.isMesh) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.textGroupWalls.clear();
      this.textGroupWalls = null;
    }
  }

  /**
   * Hide / Show measures
   */
  toggleVisibility(reset = false) {
    if (this.camera.is2DView) {
      if (reset) {
        this.visible = true;
      } else {
        this.visible = !this.visible;
      }

      // Parcourir les mesures des objets
      this.measureObjectsShow.forEach((measureGroups) => {
        measureGroups.lines.visible = this.visible;
        measureGroups.text.visible = this.visible;
      });

      // Gérer les mesures des murs
      if (this.linesGroupWalls) this.linesGroupWalls.visible = this.visible;
      if (this.textGroupWalls) this.textGroupWalls.visible = this.visible;
    }
  }

  /* updateDistances() {
    this.objectManager.getObjects().forEach((object) => {
      const directions = [
        new THREE.Vector3(1, 0, 0),  // Droite
        new THREE.Vector3(-1, 0, 0), // Gauche
        new THREE.Vector3(0, 0, 1),  // Avant
        new THREE.Vector3(0, 0, -1), // Arrière
      ];
  
      directions.forEach((direction) => {
        const raycaster = new THREE.Raycaster(object.position, direction);
        const intersects = raycaster.intersectObjects(this.room.getWallsGroup().children);
  
        if (intersects.length > 0) {
          const closestWall = intersects[0].point;
          const distance = object.position.distanceTo(closestWall);
  
          // Placer une seule étiquette de distance au milieu de la ligne
          const midpoint = new THREE.Vector3()
            .addVectors(object.position, closestWall)
            .multiplyScalar(0.5);
  
          const distanceLabel = this.createDistanceLabel(midpoint, `${(distance * 1000).toFixed(2)} mm`);
          this.scene.add(distanceLabel);
  
          // Créer une ligne de mesure entre l'objet et le mur
          const geometry = new THREE.BufferGeometry().setFromPoints([object.position, closestWall]);
          const material = new THREE.LineBasicMaterial({ color: 0xffffff });
          const line = new THREE.Line(geometry, material);
          this.scene.add(line);
        }
      });
    });
  } */
}
